import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { StepConfig, FieldConfig } from '../field-config';
import { FormbuilderService } from '../formbuilder.service';
import { FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatStepper } from '@angular/material/stepper';
import { ServiceCodeEnum } from '../../../../core/enums/service.enum';
import { ValidationService } from '../../../../core/services/validations.service';
import { NotifyService } from '../../../../core/services/notify.service';
import { getMainBusinessActivities, BusinessSectorEnum } from '../../../../core/enums/company.enum';

@Component({
  selector: 'app-formbuilder',
  templateUrl: './formbuilder.component.html',
  styleUrls: ['./formbuilder.component.scss'],
})
export class FormbuilderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() steps: StepConfig[] = [];
  @Input() initialData: any = null;
  @Input() formName = '';
  @Input() isLoading = false;
  @Input() columns = 3;
  @Input() isPaymentRequired = true;
  @Input() isEditMode = false;
  @Input() referenceNumber = '';
  @Input() formSelectData: any = {};
  @Output() submitForm = new EventEmitter<any>();
  @Output() formChange = new EventEmitter<any>();
  @Output() validated = new EventEmitter<{ kind: string; data: any }>();

  form!: FormGroup;
  stepGroups: FormGroup[] = [];
  private subs: Subscription[] = [];
  hideSubmit = false;
  isFormReady = false;
  currentStepIndex = 0;
  generatedReferenceNumber = '';
  // Prevent patchInitial loops when parent echoes our emitted payload back as initialData
  private suppressNextInitialPatch = false;

  @ViewChild('stepper') stepper!: MatStepper;

  get visibleSteps(): StepConfig[] {
    if (!this.stepGroups || this.stepGroups.length === 0) {
      return this.steps;
    }
    return this.steps.filter((step, index) => this.isStepVisible(step, index));
  }

  constructor(
    private fbSvc: FormbuilderService,
    private router: Router,
    private validationService: ValidationService,
    private notify: NotifyService,
  ) {
    // Submit button is always shown unless explicitly hidden via @Input
  }

  ngOnInit(): void {
    this.initializeForm();
    const isLicensing = this.router.url.includes('licensing');
    const serviceCode = isLicensing ? ServiceCodeEnum.LICENSING : ServiceCodeEnum.COMPANY_REGISTRATION;
    // Generate reference number if not provided
    if (!this.referenceNumber) {
      this.generateReferenceNumber(serviceCode);
    } else {
      this.generatedReferenceNumber = this.referenceNumber;
    }
  }

  private generateReferenceNumber(serviceCode: string): any {
    this.fbSvc.generateReferenceNumber(serviceCode).subscribe((res) => {
      this.generatedReferenceNumber = res.data?.referenceNumber;
    });
  }

  getReferenceNumber(): string {
    return this.generatedReferenceNumber || this.referenceNumber;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['steps'] && !changes['steps'].firstChange) {
      this.rebuildForm();
    } else if (changes['initialData'] && !changes['initialData'].firstChange && this.form) {
      // Guard against feedback loop: if initialData comes right after our emit, skip once
      if (this.suppressNextInitialPatch) {
        this.suppressNextInitialPatch = false;
      } else {
        this.patchInitial(changes['initialData'].currentValue);
      }
    }
  }

  ngOnDestroy(): void {
    this.cleanupSubscriptions();
  }

  private cleanupSubscriptions(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.subs = [];
  }

  private initializeForm(): void {
    this.rebuildForm();
  }

  private rebuildForm(): void {
    this.cleanupSubscriptions();
    this.form = this.fbSvc.buildFormFromSteps(this.steps);
    this.stepGroups = Object.keys(this.form.controls).map((k) => this.form.get(k) as FormGroup);
    if (this.initialData) this.patchInitial(this.initialData);
    this.setupConditionalVisibility();
    this.currentStepIndex = 0; // Reset to first step
    this.isFormReady = true;
  }

  setupConditionalVisibility(): void {
    this.steps.forEach((step, stepIndex) => {
      const group = this.stepGroups[stepIndex];
      const sub = group.valueChanges
        .pipe(
          debounceTime(100), // Throttle to avoid excessive calls while typing
        )
        .subscribe(() => {
          this.steps.forEach((s, idx) => {
            this.updateConditionalValidators(s, idx);
          });
          // Removed handleDynamicOptions from here - it now has its own specific subscription
          this.emitFormChange();
        });
      this.subs.push(sub);
    });

    this.steps.forEach((step, stepIndex) => {
      this.updateConditionalValidators(step, stepIndex);
      // Set up dynamic options listeners (one-time setup with their own subscriptions)
      this.handleDynamicOptions(step, stepIndex);
    });
  }

  private handleDynamicOptions(step: StepConfig, stepIndex: number): void {
    const group = this.stepGroups[stepIndex];

    this.setupBusinessSectorListener(step, stepIndex, group);

    this.handleCompanyShareTypeBreakdown(step, group);

    this.setupFlattenedShareCalculation(step, group);

    this.handleShareholderShareTypeOptions(step, group);
  }

  private setupBusinessSectorListener(step: StepConfig, stepIndex: number, group: FormGroup): void {
    const businessSectorControl = group.get('businessSector');
    const mainActivityField = step.fields.find((f) => f.name === 'mainBusinessActivity');

    if (!businessSectorControl || !mainActivityField) {
      return;
    }

    let lastSector: BusinessSectorEnum | null = businessSectorControl.value as BusinessSectorEnum;

    if (lastSector) {
      mainActivityField.options = getMainBusinessActivities(lastSector);
    }

    const sub = businessSectorControl.valueChanges
      .pipe(debounceTime(100), distinctUntilChanged())
      .subscribe((selectedSector: BusinessSectorEnum) => {
        if (lastSector === selectedSector) {
          return;
        }

        lastSector = selectedSector;

        mainActivityField.options = getMainBusinessActivities(selectedSector);

        const activityControl = group.get('mainBusinessActivity');
        if (activityControl && selectedSector) {
          const currentValue = activityControl.value;
          // Handle both single value and array of values
          if (Array.isArray(currentValue)) {
            const validValues = currentValue.filter(val =>
              mainActivityField.options?.some((opt) => opt.value === val)
            );
            if (validValues.length !== currentValue.length) {
              activityControl.setValue(validValues.length > 0 ? validValues : [], { emitEvent: false });
            }
          } else {
            const isValidOption = mainActivityField.options?.some(
              (opt) => opt.value === currentValue,
            );
            if (!isValidOption) {
              activityControl.setValue(mainActivityField.multiple ? [] : null, { emitEvent: false });
            }
          }
        }
      });

    this.subs.push(sub);
  }

  /**
   * auto-calculation for flattened share structure
   */
  private setupFlattenedShareCalculation(step: StepConfig, group: FormGroup): void {
    const ordinarySharesControl = group.get('ordinaryShares');
    const preferenceSharesControl = group.get('preferenceShares');
    const redeemableSharesControl = group.get('redeemableShares');
    const nonVotingSharesControl = group.get('nonVotingShares');
    const unallocatedSharesControl = group.get('unallocatedShares');

    const ordinaryParValueControl = group.get('ordinarySharesParValue');
    const preferenceParValueControl = group.get('preferenceSharesParValue');
    const redeemableParValueControl = group.get('redeemableSharesParValue');
    const nonVotingParValueControl = group.get('nonVotingSharesParValue');
    const unallocatedParValueControl = group.get('unallocatedSharesParValue');

    const totalSharesControl = group.get('totalShares');
    const totalValueControl = group.get('totalValue');

    const groupAny = group as any;
    if (
      ordinarySharesControl &&
      preferenceSharesControl &&
      redeemableSharesControl &&
      nonVotingSharesControl &&
      totalSharesControl &&
      totalValueControl &&
      !groupAny._flattenedShareCalculationSetup
    ) {
      const shareControls = [
        ordinarySharesControl,
        preferenceSharesControl,
        redeemableSharesControl,
        nonVotingSharesControl,
        ...(unallocatedSharesControl ? [unallocatedSharesControl] : []),
      ];

      const parValueControls = [
        ordinaryParValueControl,
        preferenceParValueControl,
        redeemableParValueControl,
        nonVotingParValueControl,
        ...(unallocatedParValueControl ? [unallocatedParValueControl] : []),
      ];

      const calculateTotals = () => {
        const totalShares =
          (Number(ordinarySharesControl.value) || 0) +
          (Number(preferenceSharesControl.value) || 0) +
          (Number(redeemableSharesControl.value) || 0) +
          (Number(nonVotingSharesControl.value) || 0) +
          (Number(unallocatedSharesControl?.value) || 0);

        const totalValue =
          (Number(ordinarySharesControl.value) || 0) *
          (Number(ordinaryParValueControl?.value) || 0) +
          (Number(preferenceSharesControl.value) || 0) *
          (Number(preferenceParValueControl?.value) || 0) +
          (Number(redeemableSharesControl.value) || 0) *
          (Number(redeemableParValueControl?.value) || 0) +
          (Number(nonVotingSharesControl.value) || 0) *
          (Number(nonVotingParValueControl?.value) || 0) +
          (Number(unallocatedSharesControl?.value) || 0) *
          (Number(unallocatedParValueControl?.value) || 0);

        const roundedTotalValue = Math.round(totalValue * 100) / 100;

        totalSharesControl.setValue(totalShares, { emitEvent: false });
        totalValueControl.setValue(roundedTotalValue, { emitEvent: false });
      };

      [...shareControls, ...parValueControls.filter((c) => c)].forEach((control) => {
        if (control) {
          const sub = control.valueChanges.pipe(debounceTime(300)).subscribe(() => {
            calculateTotals();
          });
          this.subs.push(sub);
        }
      });

      groupAny._flattenedShareCalculationSetup = true;

      calculateTotals();
    }
  }

  private handleCompanyShareTypeBreakdown(step: StepConfig, group: FormGroup): void {
    const shareTypeControl = group.get('shareTypeSelection');
    const shareTypeBreakdownGroup = group.get('shareTypeBreakdown') as FormGroup;
    const totalSharesControl = group.get('totalShares');

    if (!shareTypeControl || !shareTypeBreakdownGroup) return;

    const selectedShareTypes = shareTypeControl.value || [];

    // Map share type enums to field names
    const shareTypeFieldMap: Record<string, string> = {
      ORDINARY: 'ordinarySharesTotal',
      PREFERENCE: 'preferenceSharesTotal',
      REDEEMABLE: 'redeemableSharesTotal',
      NON_VOTING: 'nonVotingSharesTotal',
    };

    // Show/hide breakdown fields based on selected share types
    Object.entries(shareTypeFieldMap).forEach(([shareType, fieldName]) => {
      const control = shareTypeBreakdownGroup.get(fieldName);

      if (control) {
        if (selectedShareTypes.includes(shareType)) {
          if (control.disabled) {
            control.enable({ emitEvent: false });
          }
        } else {
          if (control.enabled) {
            control.setValue(0, { emitEvent: false });
            control.disable({ emitEvent: false });
          }
        }
        control.updateValueAndValidity({ emitEvent: false });
      }
    });

    // Setup auto-calculation for total shares if not already set up
    const groupAny = shareTypeBreakdownGroup as any;
    if (!groupAny._totalSharesCalculationSetup && totalSharesControl) {
      Object.values(shareTypeFieldMap).forEach((fieldName) => {
        const control = shareTypeBreakdownGroup.get(fieldName);
        if (control) {
          const sub = control.valueChanges.pipe(debounceTime(300)).subscribe(() => {
            let total = 0;
            Object.values(shareTypeFieldMap).forEach((fn) => {
              const c = shareTypeBreakdownGroup.get(fn);
              if (c && c.enabled) {
                total += Number(c.value) || 0;
              }
            });
            totalSharesControl.setValue(total, { emitEvent: false });
          });
          this.subs.push(sub);
        }
      });
      groupAny._totalSharesCalculationSetup = true;

      // Initial calculation
      let total = 0;
      Object.values(shareTypeFieldMap).forEach((fn) => {
        const c = shareTypeBreakdownGroup.get(fn);
        if (c && c.enabled) {
          total += Number(c.value) || 0;
        }
      });
      totalSharesControl.setValue(total, { emitEvent: false });
    }
  }

  private handleShareholderShareTypeOptions(step: StepConfig, group: FormGroup): void {
    const shareTypeControl = group.get('shareTypeSelection');
    const shareholdersArray = group.get('shareholders') as FormArray;

    if (!shareTypeControl || !shareholdersArray) return;

    const selectedShareTypes = shareTypeControl.value || [];

    // Create options from selected share types
    const shareTypeOptions = selectedShareTypes.map((type: string) => {
      const labels: Record<string, string> = {
        ORDINARY: 'Ordinary Shares',
        PREFERENCE: 'Preference Shares',
        REDEEMABLE: 'Redeemable Shares',
        NON_VOTING: 'Non-Voting Shares',
      };
      return {
        label: labels[type] || type,
        value: type,
      };
    });

    // Update share type options in each shareholder's shareHoldings array
    const shareholdersField = step.fields.find((f) => f.name === 'shareholders');
    if (shareholdersField && shareholdersField.children) {
      const shareHoldingsField = shareholdersField.children.find((f) => f.name === 'shareHoldings');
      if (shareHoldingsField && shareHoldingsField.children) {
        const shareTypeField = shareHoldingsField.children.find((f) => f.name === 'shareType');
        if (shareTypeField) {
          shareTypeField.options = shareTypeOptions;
        }
      }
    }
  }

  private updateConditionalValidators(step: StepConfig, stepIndex: number): void {
    const group = this.stepGroups[stepIndex];

    step.fields.forEach((field) => {
      if (field.showIf) {
        const control = group.get(field.name);
        if (control) {
          const isVisible = this.isFieldVisible(field, stepIndex);

          if (isVisible) {
            control.enable({ emitEvent: false });
            if (field.validators) {
              control.setValidators(field.validators);
            }
            control.updateValueAndValidity({ emitEvent: false });
          } else {
            control.disable({ emitEvent: false });
            control.clearValidators();
            control.reset();
            control.setErrors(null);
            control.updateValueAndValidity({ emitEvent: false });
          }
        }
      }

      if (field.type === 'group' && field.children) {
        const groupControl = group.get(field.name) as FormGroup;
        if (groupControl) {
          field.children?.forEach((childField) => {
            if (childField.showIf) {
              const childControl = groupControl.get(childField.name);
              if (childControl) {
                let isVisible = false;

                if (Array.isArray(childField.showIf)) {
                  isVisible = childField.showIf.every(condition => {
                    const parentValue = groupControl.get(condition.field)?.value;
                    return parentValue === condition.value;
                  });
                } else {
                  const parentValue = groupControl.get(childField.showIf.field)?.value;
                  isVisible = parentValue === childField.showIf.value;
                }

                if (isVisible) {
                  childControl.enable({ emitEvent: false });
                  if (childField.validators) {
                    childControl.setValidators(childField.validators);
                  }
                  childControl.updateValueAndValidity({ emitEvent: false });
                } else {
                  childControl.disable({ emitEvent: false });
                  childControl.clearValidators();
                  childControl.setErrors(null);
                  childControl.updateValueAndValidity({ emitEvent: false });
                }
              }
            }
          });
        }
      }
    });
  }

  emitFormChange(): void {
    const payload: any = {};
    this.stepGroups.forEach((g, idx) => {
      payload[`step${idx}`] = g.getRawValue();
    });
    this.suppressNextInitialPatch = true;
    this.formChange.emit(payload);
  }

  isFieldVisible(field: FieldConfig, stepIndex: number): boolean {
    if (field.visible === false) {
      return false;
    }

    if (!field.showIf) return true;

    const group = this.stepGroups[stepIndex];

    if (Array.isArray(field.showIf)) {
      return field.showIf.every(condition => {
        let targetControl = group.get(condition.field);

        if (!targetControl) {
          for (let i = 0; i < this.stepGroups.length; i++) {
            if (i !== stepIndex) {
              targetControl = this.stepGroups[i].get(condition.field);
              if (targetControl) {
                break;
              }
            }
          }
        }

        if (!targetControl) {
          return true;
        }

        return targetControl.value === condition.value;
      });
    }

    // Handle single condition (original logic)
    let targetControl = group.get(field.showIf.field);

    if (!targetControl) {
      for (let i = 0; i < this.stepGroups.length; i++) {
        if (i !== stepIndex) {
          targetControl = this.stepGroups[i].get(field.showIf.field);
          if (targetControl) {
            break;
          }
        }
      }
    }

    if (!targetControl) {
      return true;
    }

    const isVisible = targetControl.value === field.showIf.value;
    return isVisible;
  }

  isStepVisible(step: StepConfig, stepIndex: number): boolean {
    if (!step.showIf) return true;
    if (Array.isArray(step.showIf)) {
      return step.showIf.every(condition => {
        let targetControl = null;

        for (let i = 0; i < this.stepGroups.length; i++) {
          targetControl = this.stepGroups[i].get(condition.field);
          if (targetControl) {
            break;
          }
        }
        if (!targetControl) {
          return true;
        }
        return targetControl.value === condition.value;
      });
    }
    let targetControl = null;
    for (let i = 0; i < this.stepGroups.length; i++) {
      targetControl = this.stepGroups[i].get(step.showIf.field);
      if (targetControl) {
        break;
      }
    }

    if (!targetControl) {
      return true;
    }

    const isVisible = targetControl.value === step.showIf.value;
    return isVisible;
  }

  patchInitial(data: any) {
    if (!data || !this.stepGroups.length) return;

    const dataKeys = Object.keys(data);
    if (dataKeys.length === 0) {
      return;
    }

    this.stepGroups.forEach((g, idx) => {
      const key = `step${idx}`;
      const stepData = data[key] || data;

      if (stepData) {
        Object.keys(stepData).forEach((controlName) => {
          const control = g.get(controlName);
          const dataValue = stepData[controlName];

          if (control instanceof FormArray && Array.isArray(dataValue)) {
            while (control.length > 0) {
              control.removeAt(0);
            }

            const step = this.steps[idx];
            const fieldConfig = step?.fields.find((f) => f.name === controlName);

            if (fieldConfig && fieldConfig.type === 'array') {
              dataValue.forEach((itemData: any) => {
                const newGroup = this.fbSvc.addArrayItem(control, fieldConfig);
                try {
                  newGroup.patchValue(itemData, { emitEvent: false });

                  const businessSectorControl = newGroup.get('businessSector');
                  const mainActivityControl = newGroup.get('mainBusinessActivity');
                  if (businessSectorControl && mainActivityControl && itemData.businessSector) {
                    const options = getMainBusinessActivities(itemData.businessSector);
                    (mainActivityControl as any)._dynamicOptions = options;
                  }
                } catch (error) {
                  console.warn(`Error patching array item in ${controlName}:`, error);
                }
              });
            }
          }
        });
        try {
          g.patchValue(stepData, { emitEvent: false });
        } catch (error) {
          console.warn(`Error patching step ${idx}:`, error);
        }
      }
    });
  }

  getColSpanClass(field: FieldConfig): string {
    if (field.cols !== undefined) {
      const requestedCols = Math.min(field.cols, this.columns);
      if (requestedCols === 3) return 'span-3';
      if (requestedCols === 2) return 'span-2';
      if (requestedCols === 1) return 'span-1';
    }
    const fullWidthTypes = ['textarea', 'array', 'group'];
    if (fullWidthTypes.includes(field.type)) {
      if (this.columns === 2) return 'span-2';
      return 'span-3';
    }
    if (field.type === 'file') {
      if (this.columns >= 2) return 'span-2';
      return 'span-1';
    }
    return 'span-1';
  }

  getGridColumn(field: FieldConfig): string {
    const requested = (field && field.cols) || 1;
    const span = Math.max(1, Math.min(requested, this.columns || 1));
    return `span ${span}`;
  }

  onSubmit() {
    this.isLoading = true;
    const payload = this.getFormPayload();
    this.isLoading = false;
    this.submitForm.emit(payload);
  }

  private getFormPayload(): any {
    const payload: any = {
      referenceNumber: this.getReferenceNumber(),
    };
    this.stepGroups.forEach((g, index) => {
      payload[`step${index}`] = g.getRawValue();
    });
    return payload;
  }

  onStepChange(event: any): void {
    this.currentStepIndex = event.selectedIndex;
  }

  onNextStep(stepIndex: number): void {
    if (this.canProceedToNextStep(stepIndex)) {
      this.stepper.next();
    }
  }

  onSubmitClick(): void {
    const lastStepIndex = this.steps.length - 1;
    if (this.canProceedToNextStep(lastStepIndex)) {
      this.onSubmit();
    }
  }

  canProceedToNextStep(stepIndex: number): boolean {
    const stepGroup = this.stepGroups[stepIndex];
    if (!stepGroup) {
      return false;
    }

    if (stepGroup.invalid) {
      this.markFormGroupTouched(stepGroup);

      const errors = this.collectValidationErrors(stepGroup, this.steps[stepIndex]);
      this.showValidationErrors(errors);

      return false;
    }

    const step = this.steps[stepIndex];

    if (step.customValidator) {
      const allFormData: any = {};
      this.stepGroups.forEach((g, idx) => {
        const stepData = g.getRawValue();
        Object.assign(allFormData, stepData);
      });

      const validation = step.customValidator(allFormData);
      if (!validation.isValid) {
        this.notify.notifyErrorMessage(
          'Validation Error',
          validation.message || 'Please correct the errors before proceeding.',
        );
        return false;
      }
    }

    return true;
  }

  private markFormGroupTouched(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);

      if (!control) return;

      control.markAsTouched();

      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private collectValidationErrors(formGroup: FormGroup, step: StepConfig): string[] {
    const errors: string[] = [];
    const maxErrors = 5;

    for (const field of step.fields) {
      if (field.type === 'array') {
        const arrayControl = formGroup.get(field.name) as FormArray;
        if (arrayControl) {
          this.collectArrayErrors(arrayControl, field, errors, maxErrors);
        }
      } else if (field.type === 'group') {
        const groupControl = formGroup.get(field.name) as FormGroup;
        if (groupControl) {
          this.collectGroupErrors(groupControl, field, errors, maxErrors);
        }
      } else {
        const control = formGroup.get(field.name);
        if (control && control.invalid && !field.readOnly) {
          const fieldLabel = field.label || field.name;
          const errorMsg = this.formatFieldError(fieldLabel, control);
          if (errorMsg) {
            errors.push(errorMsg);
          }
        }
      }

      if (errors.length >= maxErrors) break;
    }

    return errors;
  }

  private formatFieldError(fieldLabel: string, control: AbstractControl): string {
    const rawError = this.validationService.getErrorMessage(control);
    if (!rawError) return '';

    if (rawError === 'This field is required.') {
      return `${fieldLabel} is required`;
    }

    const customizedError = rawError.replace('This field', fieldLabel);

    return customizedError.replace(/\.$/, '');
  }

  private collectArrayErrors(
    arrayControl: FormArray,
    field: FieldConfig,
    errors: string[],
    maxErrors: number,
  ): void {
    arrayControl.controls.forEach((itemControl, index) => {
      if (errors.length >= maxErrors) return;

      if (itemControl instanceof FormGroup) {
        field.children?.forEach((childField) => {
          if (errors.length >= maxErrors) return;

          const childControl = itemControl.get(childField.name);
          if (childControl && childControl.invalid && !childField.readOnly) {
            const fieldLabel = childField.label || childField.name;
            const errorMsg = this.formatFieldError(fieldLabel, childControl);
            if (errorMsg) {
              errors.push(`${field.label} #${index + 1} - ${errorMsg}`);
            }
          }
        });
      }
    });
  }

  private collectGroupErrors(
    groupControl: FormGroup,
    field: FieldConfig,
    errors: string[],
    maxErrors: number,
  ): void {
    field.children?.forEach((childField) => {
      if (errors.length >= maxErrors) return;

      const childControl = groupControl.get(childField.name);
      if (childControl && childControl.invalid && !childField.readOnly) {
        const fieldLabel = childField.label || childField.name;
        const errorMsg = this.formatFieldError(fieldLabel, childControl);
        if (errorMsg) {
          errors.push(`${field.label} - ${errorMsg}`);
        }
      }
    });
  }

  private showValidationErrors(errors: string[]): void {
    if (errors.length === 0) {
      this.notify.notifyErrorMessage('Please fill in all required fields.');
      return;
    }

    const errorCount = errors.length;
    const displayErrors = errors.slice(0, 5);
    const remainingCount = errorCount - displayErrors.length;

    let message = displayErrors.join('\n');
    if (remainingCount > 0) {
      message += `\n\n...and ${remainingCount} more error${remainingCount > 1 ? 's' : ''}`;
    }

    this.notify.notifyErrorMessage('Please Fix the Following Errors', message);

    this.scrollToFirstError();
  }

  private scrollToFirstError(): void {
    setTimeout(() => {
      const firstInvalidControl = document.querySelector('.ng-invalid:not(form):not(mat-step)');
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        if (
          firstInvalidControl instanceof HTMLInputElement ||
          firstInvalidControl instanceof HTMLTextAreaElement ||
          firstInvalidControl instanceof HTMLSelectElement
        ) {
          firstInvalidControl.focus();
        }
      }
    }, 100);
  }

  getStepIcon(index: number): string {
    const icons = [
      'business',
      'people',
      'description',
      'account_balance',
      'verified',
      'assignment_turned_in',
    ];
    return icons[index] || 'edit_note';
  }
}
